import React, { useState, useCallback } from 'react';
import { Controller, useForm } from 'react-hook-form';
import { Trans, useTranslation } from 'react-i18next';
import Textarea from 'react-textarea-autosize';
import InformationCircleIcon from '@heroicons/react/24/solid/InformationCircleIcon';
import Button from '@medflex/components/stories/Button';
import InputField from '@medflex/components/stories/InputField';
import Modal from '@medflex/components/stories/Modal';
import Tooltip from '@medflex/components/stories/Tooltip';
import { createInternalNameForUserTagName } from '@medflex/shared-utils';
import {
  USER_TAGS_DESCRIPTION_MAX_LENGTH,
  USER_TAGS_NAME_MAX_LENGTH,
} from '@medflex/shared-constants';
import InputRadio from '@medflex/components/stories/InputRadio';
import { PlusCircleIcon } from '@heroicons/react/24/outline';
import { showAlertToast } from '@medflex/components/stories/Toast';
import { UserTagTypes } from '@medflex/shared-types';
import InputCheckbox from '@medflex/components/stories/InputCheckbox';
import useLocationSearchParams from '../../hooks/useLocationSearchParams';
import useModal from '../hooks/useModal';
import useUserProfile from '../hooks/useUserProfile';
import UserTagKeywords from './UserTagKeywords';

const NEW_GROUP_ID = 'newGroupIdTemporaryValueId';
const NEW_GROUP_INPUT_NAME = 'newGroupName';

const UserTagsEditModal = () => {
  const { close } = useModal();
  const { t } = useTranslation('physician');
  const [isLoading, setIsLoading] = useState(false);
  const [isGroupCreationActive, setIsGroupCreationActive] = useState(false);
  const { id } = useLocationSearchParams();
  const { userTags, userTagGroups, createTag, updateTag } = useUserProfile();
  const selectedTagForEdit = userTags[id];
  const isKeyEditable = !selectedTagForEdit || selectedTagForEdit.type !== UserTagTypes.SYSTEM;

  const {
    handleSubmit: wrapSubmit,
    control,
    errors,
    getValues,
    formState,
    register,
    watch,
  } = useForm({
    mode: 'onChange',
    defaultValues: {
      tagGroupActive: Boolean(selectedTagForEdit?.groupId),
      keywordsActive: Boolean(selectedTagForEdit?.keywords.length),
    },
  });
  const isTagGroupSelectionActive = watch('tagGroupActive');
  const isKeywordsSectionActive = watch('keywordsActive');

  const handleSubmit = useCallback(
    async formData => {
      setIsLoading(true);

      const payload = {
        id,
        key: formData.key || selectedTagForEdit?.key,
        name: formData.name,
        description: formData.description || null,
        ...(formData[NEW_GROUP_INPUT_NAME] && { groupName: formData[NEW_GROUP_INPUT_NAME] }),
        ...(!formData[NEW_GROUP_INPUT_NAME] && formData.groupId && { groupId: formData.groupId }),
        keywords: formData.keywordsActive ? formData.keywords : [],
      };

      try {
        if (id) {
          await updateTag(payload);
        } else {
          await createTag(payload);
        }
        close({ modal: 'userTagsEdit', id });
      } catch (error) {
        setIsLoading(false);
        showAlertToast(
          t('toasts.user_tags.post.failure.title'),
          t('toasts.user_tags.post.failure.text'),
          {
            toastId: 'userTagsPostFailed',
          },
        );
      }
    },
    [close, createTag, id, selectedTagForEdit?.key, t, updateTag],
  );

  const title = selectedTagForEdit
    ? t('modals.user_tags.creation.title_adjust')
    : t('modals.user_tags.creation.title_new');

  const handleOnClose = useCallback(() => {
    close({ modal: 'userTagsEdit', id });
  }, [close, id]);

  const checkTagNameForDuplication = useCallback(
    name =>
      !Object.values(userTags).find(
        ({ value, id }) =>
          (selectedTagForEdit &&
            selectedTagForEdit.id !== id &&
            createInternalNameForUserTagName(name) === createInternalNameForUserTagName(value)) ||
          (!selectedTagForEdit?.id &&
            createInternalNameForUserTagName(name) === createInternalNameForUserTagName(value)),
      ),
    [selectedTagForEdit, userTags],
  );

  const checkGroupNameForDuplication = useCallback(
    value => !Object.values(userTagGroups).find(({ value: groupValue }) => groupValue === value),
    [userTagGroups],
  );

  return (
    <Modal title={title} showCloseButton onClickCloseButton={handleOnClose}>
      <div data-component="UserTagsEditModal">
        <form onSubmit={wrapSubmit(handleSubmit)} className="flex flex-1 flex-col space-y-4">
          {/* eslint-disable-next-line jsx-a11y/label-has-associated-control */}

          <Controller
            control={control}
            name="name"
            defaultValue={selectedTagForEdit?.value || ''}
            rules={{
              required: true,
              maxLength: USER_TAGS_NAME_MAX_LENGTH,
              validate: { duplicateName: checkTagNameForDuplication },
            }}
            render={({ onChange, onBlur, value, name }, { invalid }) => (
              <div>
                <InputField
                  id={name}
                  label={t('modals.user_tags.creation.inputs.name.label')}
                  name={name}
                  onBlur={onBlur}
                  onChange={({ target }) => onChange(target.value)}
                  state={invalid ? 'invalid' : 'default'}
                  value={value}
                  className="mb-1"
                />
                {errors.name && (
                  <div className="text-xs text-red-600 mb-1">
                    {t(`modals.user_tags.creation.inputs.name.errors.${errors.name.type}`, {
                      value,
                      maxLength: USER_TAGS_NAME_MAX_LENGTH,
                    })}
                  </div>
                )}
                <div className="text-xs text-slate-500 space-y-1">
                  {isKeyEditable && (
                    <div className="flex">
                      <span className="mr-1">
                        {t('modals.user_tags.creation.inputs.name.internal')}
                      </span>
                      <Tooltip title={t('modals.user_tags.creation.inputs.name.internal_tooltip')}>
                        <InformationCircleIcon className="text-slate-600 w-4 h-4" />
                      </Tooltip>
                      : {createInternalNameForUserTagName(value) || '-'}
                      <input
                        name="key"
                        type="hidden"
                        value={createInternalNameForUserTagName(value)}
                        ref={register}
                      />
                    </div>
                  )}

                  <div>{t('modals.user_tags.creation.inputs.name.info')}</div>
                </div>
              </div>
            )}
          />
          <Controller
            control={control}
            name="description"
            defaultValue={selectedTagForEdit?.description || ''}
            render={({ onChange, name, value }) => (
              // eslint-disable-next-line jsx-a11y/label-has-associated-control
              <label>
                <Trans
                  parent="div"
                  t={t}
                  i18nKey="modals.user_tags.creation.inputs.description.label"
                  className="font-semibold mb-2"
                >
                  Description <span className="text-slate-500">optional</span>
                </Trans>
                <div className="border border-solid border-slate-400 rounded flex flex-grow flex-col">
                  <Textarea
                    className="resize-none self-stretch rounded p-3 leading-tight placeholder-slate-500 outline-none focus:border-blue-500"
                    id={selectedTagForEdit?.id}
                    maxRows={10}
                    minRows={4}
                    name={name}
                    onChange={val => onChange(val)}
                    readOnly={isLoading}
                    value={value}
                    maxLength={USER_TAGS_DESCRIPTION_MAX_LENGTH}
                  />
                </div>
              </label>
            )}
          />
          <div className="w-2/3 space-y-4">
            <div className="font-semibold">
              <label className="space-x-2">
                <Controller
                  control={control}
                  name="keywordsActive"
                  render={({ onChange, value }) => (
                    <InputCheckbox checked={value} onChange={e => onChange(e.target.checked)} />
                  )}
                />
                <span className="font-semibold">
                  {t('modals.user_tags.creation.inputs.keywords.label')}
                </span>
              </label>
            </div>
            {isKeywordsSectionActive && (
              <div className="px-4">
                <Controller
                  control={control}
                  name="keywords"
                  defaultValue={selectedTagForEdit?.keywords || []}
                  render={({ onChange, value: keywords }) => (
                    <UserTagKeywords keywords={keywords} onChange={onChange} />
                  )}
                />
              </div>
            )}
          </div>
          <div className="w-2/3 space-y-4">
            <div className="font-semibold">
              <label className="space-x-2">
                <Controller
                  control={control}
                  name="tagGroupActive"
                  render={({ onChange, value }) => (
                    <InputCheckbox checked={value} onChange={e => onChange(e.target.checked)} />
                  )}
                />
                <span className="font-semibold">
                  {t('modals.user_tags.creation.inputs.groupId.label')}
                </span>
              </label>
            </div>
            {isTagGroupSelectionActive && (
              <div className="px-4">
                <Controller
                  control={control}
                  name="groupId"
                  defaultValue={selectedTagForEdit?.groupId || ''}
                  render={({ name: groupIdName, onChange, value: selectedValue }) => (
                    <div className="flex flex-col border divide-y rounded">
                      {Object.values(userTagGroups).map(({ id, value }) => (
                        <label className="flex p-3" key={id}>
                          <span className="w-2/3 font-semibold">{value}</span>
                          <span className="w-1/3 text-center">
                            <InputRadio
                              name={groupIdName}
                              checked={selectedValue === id}
                              value={id}
                              onChange={val => {
                                setIsGroupCreationActive(false);
                                onChange(val);
                              }}
                            />
                          </span>
                        </label>
                      ))}
                      {!isGroupCreationActive && (
                        <Button
                          className="p-3"
                          kind="link"
                          onClick={() => {
                            onChange(NEW_GROUP_ID);
                            setIsGroupCreationActive(true);
                          }}
                        >
                          <PlusCircleIcon className="w-6 h-6" />
                          <p className="ml-2">
                            {t('modals.user_tags.creation.inputs.groupId.button_create_tag_group')}
                          </p>
                        </Button>
                      )}
                      {isGroupCreationActive && (
                        // eslint-disable-next-line jsx-a11y/label-has-associated-control
                        <label className="flex flex-wrap p-3" key={NEW_GROUP_INPUT_NAME}>
                          <div className="flex w-full mb-1 items-center">
                            <span className="w-2/3 font-semibold">
                              <Controller
                                control={control}
                                name={NEW_GROUP_INPUT_NAME}
                                defaultValue=""
                                rules={{
                                  validate: { duplicateName: checkGroupNameForDuplication },
                                }}
                                render={({ name, value, onChange }) => (
                                  <InputField name={name} value={value} onChange={onChange} />
                                )}
                              />
                            </span>
                            <span className="w-1/3 text-center">
                              <InputRadio
                                name={groupIdName}
                                checked={selectedValue === NEW_GROUP_ID}
                                value={NEW_GROUP_ID}
                                onChange={onChange}
                              />
                            </span>
                          </div>
                          {errors[NEW_GROUP_INPUT_NAME] && (
                            <div className="text-xs text-red-600 mb-1">
                              {t(
                                `modals.user_tags.creation.inputs.${NEW_GROUP_INPUT_NAME}.errors.${errors[NEW_GROUP_INPUT_NAME].type}`,
                                {
                                  value: getValues(NEW_GROUP_INPUT_NAME),
                                },
                              )}
                            </div>
                          )}
                        </label>
                      )}
                    </div>
                  )}
                />
              </div>
            )}
          </div>
          <Button
            className="my-6 self-end"
            type="submit"
            isLoading={isLoading}
            isDisabled={isLoading || !formState.isValid}
          >
            {id
              ? t('modals.user_tags.creation.button_adjust')
              : t('modals.user_tags.creation.button_new')}
          </Button>
        </form>
      </div>
    </Modal>
  );
};

export default UserTagsEditModal;
